import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import ERC20_ABI from 'abis/erc20.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { useMemo } from 'react'
import type { Abi, Address } from 'viem'
import { useReadContract } from 'wagmi'

import { useReadContractMultiple } from './useReadContractMultiple'

export function useTokenAllowance(token?: Token, owner?: string, spender?: string): CurrencyAmount<Token> | undefined {
  const { data: allowance } = useReadContract({
    address: token?.address as Address | undefined,
    abi: ERC20_ABI as Abi,
    functionName: 'allowance',
    args: owner && spender ? [owner as Address, spender as Address] : undefined,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!(token && owner && spender),
    },
  })

  return useMemo(() => {
    if (token && allowance !== undefined) {
      const allowanceAmount = CurrencyAmount.fromRawAmount(token as Token, (allowance as bigint).toString())
      return allowanceAmount
    }
    return undefined
  }, [token, allowance])
}

export function useTokenAllowances(
  tokens: (Token | null | undefined)[],
  owner?: string,
  spender?: string
): (CurrencyAmount<Token> | undefined)[] {
  const validTokens = useMemo(() => tokens.filter((token): token is Token => !!token), [tokens])

  const contracts = useMemo(() => {
    if (!owner || !spender) return []
    return validTokens.map((token) => ({
      address: token.address as Address,
      abi: ERC20_ABI as Abi,
      functionName: 'allowance' as const,
      args: [owner as Address, spender as Address] as const,
    }))
  }, [validTokens, owner, spender])

  const { data: allowances } = useReadContractMultiple({
    contracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: contracts.length > 0,
    },
  })

  return useMemo(() => {
    return tokens.map((token, index) => {
      if (!token) return undefined

      const validTokenIndex = validTokens.findIndex((t) => t.address === token.address)
      if (validTokenIndex === -1) return undefined

      const allowanceResult = allowances?.[validTokenIndex]

      if (!allowanceResult || allowanceResult.status === 'failure') return undefined

      return CurrencyAmount.fromRawAmount(token, (allowanceResult.result as bigint).toString())
    })
  }, [tokens, validTokens, allowances])
}
